import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Eye, EyeOff, Check, Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API_URL = window.location.origin;

const SetupAccountPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [token] = useState(searchParams.get('token') || '');
  const [tokenValid, setTokenValid] = useState(null);
  const [userInfo, setUserInfo] = useState({ email: '', full_name: '' });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Password requirements
  const requirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[a-z]/.test(password), text: 'One lowercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' }
  ];
  
  const allRequirementsMet = requirements.every(r => r.met);
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  // Verify token on mount
  const verifyToken = useCallback(async () => {
    if (!token) {
      setTokenValid(false);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/verify-setup-token?token=${token}`);
      setTokenValid(true);
      setUserInfo({
        email: response.data.email,
        full_name: response.data.full_name
      });
    } catch (err) {
      setTokenValid(false);
      setError(err.response?.data?.detail || 'Invalid or expired setup link');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allRequirementsMet) {
      setError('Please meet all password requirements');
      return;
    }
    
    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/auth/setup-account`, {
        token,
        password
      });

      // Auto-login with the returned token
      if (response.data.token) {
        login(response.data.token, response.data.user);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set up account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-forest mx-auto mb-4" />
            <p className="text-gray-600">Verifying your setup link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token state
  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="font-heading text-2xl text-forest mb-2">Link Expired or Invalid</h2>
            <p className="text-gray-600 mb-6">
              {error || 'This setup link is no longer valid. Please contact support for assistance.'}
            </p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full bg-forest hover:bg-forest/90">
                  Go to Login
                </Button>
              </Link>
              <a href="mailto:info@volitionenterprises.com">
                <Button variant="outline" className="w-full">
                  Contact Support
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid token - show password setup form
  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-sand/30 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 bg-forest/10 rounded-full mb-4">
            <Sparkles className="h-8 w-8 text-forest" />
          </div>
          <h1 className="font-heading text-3xl text-forest mb-2">
            Welcome to the Inner Circle
          </h1>
          <p className="text-gray-600">
            Set up your password to access your membership
          </p>
        </div>

        {/* Setup Form */}
        <Card className="shadow-xl">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Display */}
              <div>
                <Label className="text-sm text-gray-500">Your Email</Label>
                <p className="text-forest font-medium">{userInfo.email}</p>
              </div>

              {/* Password */}
              <div>
                <Label htmlFor="password">Create Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a secure password"
                    className="pr-10"
                    data-testid="setup-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-gray-700 mb-2">Password must have:</p>
                {requirements.map((req, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <div className={`h-4 w-4 rounded-full flex items-center justify-center ${req.met ? 'bg-green-500' : 'bg-gray-300'}`}>
                      {req.met && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span className={req.met ? 'text-green-700' : 'text-gray-500'}>{req.text}</span>
                  </div>
                ))}
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="mt-1"
                  data-testid="setup-confirm-password-input"
                />
                {confirmPassword && (
                  <p className={`text-sm mt-1 ${passwordsMatch ? 'text-green-600' : 'text-red-500'}`}>
                    {passwordsMatch ? '✓ Passwords match' : 'Passwords do not match'}
                  </p>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                disabled={!allRequirementsMet || !passwordsMatch || submitting}
                className="w-full bg-terracotta hover:bg-terracotta/90 h-12 text-lg"
                data-testid="setup-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Setting up...
                  </>
                ) : (
                  'Complete Setup & Enter'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Help text */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Having trouble? Email us at{' '}
          <a href="mailto:info@volitionenterprises.com" className="text-terracotta hover:underline">
            info@volitionenterprises.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default SetupAccountPage;
